import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import ComplianceMiniCalendar from '../components/ComplianceMiniCalendar';
import { Button, Card, Select, StatusPill, Table, EmptyState, useToast } from '../../design-system';
import '../styles/ComplianceDashboard.css';

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 12a8 8 0 1 1-2.6-5.9M20 4v5h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// KPI icons — one per tile, inherit the tile's accent via currentColor.
const ICONS = {
  total: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8.5 9h7M8.5 12.5h7M8.5 16h4" />
    </svg>
  ),
  overdue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3.5 21 19H3Z" /><path d="M12 9.5v4M12 16.3v.2" />
    </svg>
  ),
  inProgress: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="8.4" /><path d="M12 7.5V12l3 2" />
    </svg>
  ),
  upcoming: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 9.5h17M8 3v3.5M16 3v3.5" />
    </svg>
  ),
  filed: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="8.4" /><path d="M8.4 12.2l2.4 2.4 4.8-5" />
    </svg>
  ),
};

const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] } },
};

function ComplianceDashboard() {
  // `overview` is always the full, unfiltered set — the KPIs, calendar and
  // type summary need the whole picture. `instances` is the server-filtered set
  // that feeds the detail table only.
  const [overview, setOverview] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    fetchInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const fetchAll = async () => {
    const [overviewRes, filteredRes] = await Promise.all([
      apiClient.get('/api/instances'),
      status === 'all' ? null : apiClient.get('/api/instances', { params: { status } }),
    ]);
    setOverview(overviewRes.data);
    setInstances(filteredRes ? filteredRes.data : overviewRes.data);
    setLastUpdated(new Date());
  };

  const fetchInstances = async () => {
    try {
      setLoading(true);
      await fetchAll();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load compliance calendar');
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (instance) =>
    instance.status !== 'filed' &&
    instance.status !== 'not_applicable' &&
    instance.statutoryDueDate &&
    new Date(instance.statutoryDueDate) < new Date();

  const counts = {
    overdue: overview.filter(isOverdue).length,
    upcoming: overview.filter((i) => i.status === 'upcoming' && !isOverdue(i)).length,
    inProgress: overview.filter((i) => i.status === 'in_progress' && !isOverdue(i)).length,
    filed: overview.filter((i) => i.status === 'filed').length,
  };
  const total = overview.length;
  const pctOf = (n) => (total ? Math.round((n / total) * 100) : 0);

  const kpis = [
    { key: 'total', label: 'Total Compliances', value: total, accent: 'accent', caption: 'across every client' },
    { key: 'overdue', label: 'Overdue', value: counts.overdue, accent: 'danger', caption: total ? `${pctOf(counts.overdue)}% of total` : null },
    { key: 'inProgress', label: 'In Progress', value: counts.inProgress, accent: 'info', caption: null },
    { key: 'upcoming', label: 'Upcoming', value: counts.upcoming, accent: 'neutral', caption: null },
    { key: 'filed', label: 'Filed', value: counts.filed, accent: 'success', caption: total ? `${pctOf(counts.filed)}% on file` : null },
  ];

  // Segmented health bar (filed → in-progress → upcoming → overdue).
  const barSegments = [
    { key: 'filed', value: counts.filed, cls: 'success' },
    { key: 'inProgress', value: counts.inProgress, cls: 'info' },
    { key: 'upcoming', value: counts.upcoming, cls: 'neutral' },
    { key: 'overdue', value: counts.overdue, cls: 'danger' },
  ].filter((s) => s.value > 0);

  const summaryByType = useMemo(() => {
    const buckets = new Map();
    overview.forEach((i) => {
      const name = i.enrollmentId?.complianceTypeId?.name || 'Unknown';
      if (!buckets.has(name)) buckets.set(name, { id: name, name, due: 0, overdue: 0, filed: 0 });
      const bucket = buckets.get(name);
      if (isOverdue(i)) bucket.overdue += 1;
      else if (i.status === 'filed' || i.status === 'late_filed') bucket.filed += 1;
      else bucket.due += 1;
    });
    return Array.from(buckets.values()).sort(
      (a, b) => b.due + b.overdue + b.filed - (a.due + a.overdue + a.filed)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview]);

  const summaryColumns = [
    { key: 'name', header: 'Type', render: (r) => <span className="cc-summary-type">{r.name}</span> },
    { key: 'due', header: 'Due', render: (r) => r.due || <span className="cc-muted">0</span> },
    { key: 'overdue', header: 'Overdue', render: (r) => (r.overdue > 0 ? <span className="cc-num-danger">{r.overdue}</span> : <span className="cc-muted">0</span>) },
    { key: 'filed', header: 'Filed', render: (r) => (r.filed > 0 ? <span className="cc-num-success">{r.filed}</span> : <span className="cc-muted">0</span>) },
  ];

  const columns = [
    {
      key: 'client',
      header: 'Client',
      render: (i) =>
        i.clientId?._id ? (
          <button className="cc-client-link" onClick={() => navigate(`/org-admin/clients/${i.clientId._id}`)}>
            {i.clientId.displayName}
          </button>
        ) : ('—'),
    },
    { key: 'compliance', header: 'Compliance', render: (i) => i.enrollmentId?.complianceTypeId?.name || '—' },
    { key: 'period', header: 'Period', render: (i) => i.periodLabel },
    {
      key: 'due',
      header: 'Statutory Due',
      render: (i) =>
        i.statutoryDueDate ? new Date(i.statutoryDueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="cc-muted">Not set</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => (isOverdue(i) ? <StatusPill variant="danger" label="Overdue" /> : <StatusPill status={i.status} />),
    },
  ];

  return (
    <OrgAdminLayout
      title="Compliance Calendar"
      subtitle="Every due date across every client"
      headerRight={
        <div className="cc-header-actions">
          {lastUpdated && (
            <span className="cc-last-updated">
              Updated {lastUpdated.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button variant="secondary" size="sm" iconLeft={<RefreshIcon />} onClick={fetchInstances} loading={loading}>
            Refresh
          </Button>
        </div>
      }
    >
      <div className="cc-page">
        {/* KPI band */}
        <motion.div className="cc-kpis" variants={containerVariants} initial="hidden" animate="visible">
          {kpis.map((k) => (
            <motion.div key={k.key} variants={itemVariants} className={`cc-kpi cc-accent-${k.accent}`}>
              <span className="cc-kpi-icon">{ICONS[k.key]}</span>
              <div className="cc-kpi-body">
                <span className="cc-kpi-value">{k.value}</span>
                <span className="cc-kpi-label">{k.label}</span>
                {k.caption && <span className="cc-kpi-caption">{k.caption}</span>}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Health strip */}
        {total > 0 && (
          <Card className="cc-health" padding="md">
            <div className="cc-health-bar" role="img" aria-label="Compliance status mix">
              {barSegments.length === 0 ? (
                <span className="cc-health-seg neutral" style={{ flex: 1 }} />
              ) : (
                barSegments.map((s) => <span key={s.key} className={`cc-health-seg ${s.cls}`} style={{ flex: s.value }} title={`${s.value}`} />)
              )}
            </div>
            <div className="cc-health-legend">
              <span><i className="cc-dot success" />{counts.filed} filed</span>
              <span><i className="cc-dot info" />{counts.inProgress} in progress</span>
              <span><i className="cc-dot neutral" />{counts.upcoming} upcoming</span>
              <span><i className="cc-dot danger" />{counts.overdue} overdue</span>
            </div>
          </Card>
        )}

        {/* Two-column body: main content + calendar aside */}
        <div className="cc-body">
          <div className="cc-main">
            <Card className="cc-card" padding="md">
              <Card.Header title="Compliance Summary" subtitle="Every compliance type, across every client" />
              {loading && overview.length === 0 ? (
                <Table columns={summaryColumns} data={[]} keyField="id" loading />
              ) : summaryByType.length === 0 ? (
                <EmptyState icon="📅" title="No compliance instances yet" description="Enroll a client into a compliance to populate the calendar." />
              ) : (
                <div className="cc-table-scroll">
                  <Table columns={summaryColumns} data={summaryByType} keyField="id" />
                </div>
              )}
            </Card>

            <Card className="cc-card" padding="md">
              <div className="cc-detail-head">
                <div>
                  <h3 className="cc-detail-title">All Compliance Instances</h3>
                  <p className="cc-detail-sub">Filter by status to narrow the list</p>
                </div>
                <Select value={status} onChange={(e) => setStatus(e.target.value)} className="cc-status-filter" aria-label="Filter by status">
                  <option value="all">All statuses</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="in_progress">In Progress</option>
                  <option value="filed">Filed</option>
                  <option value="late_filed">Late Filed</option>
                  <option value="not_applicable">Not Applicable</option>
                </Select>
              </div>
              {!loading && instances.length === 0 ? (
                <EmptyState icon="🔍" title="Nothing here" description="No compliance instances match this filter." />
              ) : (
                <div className="cc-table-scroll">
                  <Table
                    columns={columns}
                    data={instances}
                    loading={loading}
                    rowClassName={(i) => (isOverdue(i) ? 'ds-table-row-overdue' : '')}
                  />
                </div>
              )}
            </Card>
          </div>

          <aside className="cc-aside">
            <ComplianceMiniCalendar instances={overview} isOverdue={isOverdue} />
          </aside>
        </div>
      </div>
    </OrgAdminLayout>
  );
}

export default ComplianceDashboard;
